/**
 * journeyAgent.js
 * Custom React hooks for the Transaction Journey feature.
 * Provides data fetching and mutations for buyer journeys, milestones, and progress tracking.
 */

import { useState, useEffect, useCallback } from 'react';
import APIAgent from './apiAgent';
import asArray from '../utils/asArray';

// ---------------------------------------------------------------------------
// useBuyerJourney — load all journeys for a buyer
// ---------------------------------------------------------------------------
export function useBuyerJourney(buyerId) {
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!buyerId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await APIAgent.request(`/api/journey/buyer/${buyerId}`);
      setJourneys(asArray(data));
    } catch (err) {
      setError(err.message || 'Failed to load journeys');
    } finally {
      setLoading(false);
    }
  }, [buyerId]);

  useEffect(() => { load(); }, [load]);

  return { journeys, loading, error, reload: load };
}

// ---------------------------------------------------------------------------
// useSellerJourneys — transactions where this person is the seller
// ---------------------------------------------------------------------------
export function useSellerJourneys(sellerId) {
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!sellerId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await APIAgent.request(`/api/journey/seller/${sellerId}`);
      setJourneys(asArray(data));
    } catch (err) {
      setError(err.message || 'Failed to load journeys');
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  useEffect(() => { load(); }, [load]);

  return { journeys, loading, error, reload: load };
}

// ---------------------------------------------------------------------------
// useJourneyDetail — single journey with full detail
// ---------------------------------------------------------------------------
export function useJourneyDetail(transactionId) {
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!transactionId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await APIAgent.request(`/api/journey/${transactionId}`);
      setJourney(data);
    } catch (err) {
      setError(err.message || 'Failed to load journey detail');
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  useEffect(() => { load(); }, [load]);

  return { journey, loading, error, reload: load };
}

// ---------------------------------------------------------------------------
// useJourneyMilestones — milestones for a transaction journey
// ---------------------------------------------------------------------------
export function useJourneyMilestones(transactionId) {
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!transactionId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await APIAgent.request(`/api/journey/${transactionId}/milestones`);
      setMilestones(asArray(data));
    } catch (err) {
      setError(err.message || 'Failed to load milestones');
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  useEffect(() => { load(); }, [load]);

  return { milestones, loading, error, reload: load };
}

// ---------------------------------------------------------------------------
// initializeJourney — create/initialize a journey for a transaction
// ---------------------------------------------------------------------------
export async function initializeJourney(transactionId) {
  return APIAgent.request(`/api/journey/${transactionId}/initialize`, 'POST');
}

// ---------------------------------------------------------------------------
// updateMilestone — update a milestone's status or data
// ---------------------------------------------------------------------------
export async function updateMilestone(milestoneId, data) {
  return APIAgent.request(`/api/journey/milestones/${milestoneId}`, 'PUT', data);
}

// ---------------------------------------------------------------------------
// usePropertyJourneys — load all property interactions for a buyer
// ---------------------------------------------------------------------------
export function usePropertyJourneys(buyerId) {
  const [propertyJourneys, setPropertyJourneys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!buyerId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await APIAgent.request(`/api/journey/buyer/${buyerId}/property-journeys`);
      setPropertyJourneys(asArray(data));
    } catch (err) {
      setError(err.message || 'Failed to load property journeys');
    } finally {
      setLoading(false);
    }
  }, [buyerId]);

  useEffect(() => { load(); }, [load]);

  return { propertyJourneys, loading, error, reload: load };
}

// useHouseTransaction — look up the active transaction for a seller's house
// ---------------------------------------------------------------------------
export function useHouseTransaction(houseId) {
  const [transactionId, setTransactionId] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!houseId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    APIAgent.request(`/api/journey/house/${houseId}/transaction`)
      .then((data) => {
        if (!cancelled) {
          setTransactionId(data.transaction_id || null);
          setStatus(data.status || null);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load transaction');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [houseId]);

  return { transactionId, status, loading, error };
}

